/**
 * Batch Upload Utility
 *
 * Provides a reusable function to upload files in batches to improve
 * performance when uploading a large volume of files.
 *
 * Source - Adapted from https://stackoverflow.com/questions/41673499/how-to-upload-multiple-files-to-firebase
 * Posted by Taio
 * Retrieved 2025-11-05, License - CC BY-SA 4.0
 */

export interface BatchUploadProgress {
   currentBatch: number;
   totalBatches: number;
   currentBatchProgress: number;
   overallProgress: number;
   completedFiles: number;
   totalFiles: number;
   failedFiles: number;
}

export interface BatchUploadOptions {
   /**
    * Number of files to upload in each batch
    * @default 60
    */
   batchSize?: number;

   /**
    * Callback function called when progress is updated
    */
   onProgress?: (progress: BatchUploadProgress) => void;

   /**
    * Callback function called when a single file upload completes successfully
    */
   onFileSuccess?: (fileIndex: number, result: any) => void;

   /**
    * Callback function called when a single file upload fails
    */
   onFileError?: (fileIndex: number, error: Error) => void;

   /**
    * Callback function called when a batch completes
    */
   onBatchComplete?: (batchIndex: number, batchResults: any[]) => void;
}

/**
 * Upload files in batches using a provided upload function
 *
 * @param files - Array of files to upload
 * @param uploadFunction - Async function that handles uploading a single file
 * @param options - Configuration options for batch upload
 * @returns Promise that resolves to an array of upload results
 *
 * @example
 * ```typescript
 * const results = await batchUpload(
 *   files,
 *   async (file) => {
 *     const formData = new FormData();
 *     formData.append('photo', file);
 *     const response = await fetch('/api/upload', { method: 'POST', body: formData });
 *     return await response.json();
 *   },
 *   {
 *     batchSize: 60,
 *     onProgress: (progress) => {
 *       console.log(`Overall progress: ${progress.overallProgress}%`);
 *     }
 *   }
 * );
 * ```
 */
export async function batchUpload<T = any>(
   files: File[],
   uploadFunction: (file: File, fileIndex: number) => Promise<T>,
   options: BatchUploadOptions = {}
): Promise<(T | Error)[]> {
   const { batchSize = 60, onProgress, onFileSuccess, onFileError, onBatchComplete } = options;

   const totalFiles = files.length;
   const totalBatches = Math.ceil(totalFiles / batchSize);
   const results: (T | Error)[] = [];

   let completedFiles = 0;
   let failedFiles = 0;

   // Process files in batches
   for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const batchStart = batchIndex * batchSize;
      const batchEnd = Math.min(batchStart + batchSize, totalFiles);
      const batchFiles = files.slice(batchStart, batchEnd);
      const batchResults: (T | Error)[] = [];

      // Upload all files in current batch concurrently
      const batchPromises = batchFiles.map(async (file, indexInBatch) => {
         const fileIndex = batchStart + indexInBatch;

         try {
            const result = await uploadFunction(file, fileIndex);

            completedFiles++;

            // Calculate progress
            const overallProgress = Math.round((completedFiles / totalFiles) * 100);
            const currentBatchProgress = Math.round(((indexInBatch + 1) / batchFiles.length) * 100);

            // Call progress callback
            if (onProgress) {
               onProgress({
                  currentBatch: batchIndex + 1,
                  totalBatches,
                  currentBatchProgress,
                  overallProgress,
                  completedFiles,
                  totalFiles,
                  failedFiles,
               });
            }

            // Call success callback
            if (onFileSuccess) {
               onFileSuccess(fileIndex, result);
            }

            return result;
         } catch (error) {
            failedFiles++;
            completedFiles++;

            const uploadError = error instanceof Error ? error : new Error('Upload failed');

            // Calculate progress even for failed files
            const overallProgress = Math.round((completedFiles / totalFiles) * 100);
            const currentBatchProgress = Math.round(((indexInBatch + 1) / batchFiles.length) * 100);

            if (onProgress) {
               onProgress({
                  currentBatch: batchIndex + 1,
                  totalBatches,
                  currentBatchProgress,
                  overallProgress,
                  completedFiles,
                  totalFiles,
                  failedFiles,
               });
            }

            // Call error callback
            if (onFileError) {
               onFileError(fileIndex, uploadError);
            }

            return uploadError;
         }
      });

      // Wait for all files in this batch to complete
      const batchUploadResults = await Promise.all(batchPromises);
      batchResults.push(...batchUploadResults);
      results.push(...batchUploadResults);

      // Call batch complete callback
      if (onBatchComplete) {
         onBatchComplete(batchIndex, batchResults);
      }
   }

   return results;
}

/**
 * Upload files in batches using FormData approach
 * Each batch is sent as a single FormData request
 *
 * @param files - Array of files to upload
 * @param uploadUrl - URL endpoint to upload to
 * @param options - Configuration options
 * @returns Promise that resolves to an array of upload responses
 *
 * @example
 * ```typescript
 * const results = await batchUploadFormData(
 *   files,
 *   '/api/albums/123/upload-photos',
 *   {
 *     headers: { 'Authorization': 'Bearer token' },
 *     batchSize: 60,
 *     onProgress: (progress) => {
 *       console.log(`Batch ${progress.currentBatch}/${progress.totalBatches}`);
 *     }
 *   }
 * );
 * ```
 */
export async function batchUploadFormData(
   files: File[],
   uploadUrl: string,
   options: BatchUploadOptions & {
      headers?: Record<string, string>;
      getFormDataForBatch?: (files: File[]) => FormData;
   } = {}
): Promise<any[]> {
   const {
      batchSize = 60,
      headers = {},
      getFormDataForBatch,
      onProgress,
      onBatchComplete,
   } = options;

   const totalFiles = files.length;
   const totalBatches = Math.ceil(totalFiles / batchSize);
   const results: any[] = [];

   let completedFiles = 0;

   // Process files in batches
   for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const batchStart = batchIndex * batchSize;
      const batchEnd = Math.min(batchStart + batchSize, totalFiles);
      const batchFiles = files.slice(batchStart, batchEnd);

      try {
         // Create FormData for this batch
         const formData = getFormDataForBatch
            ? getFormDataForBatch(batchFiles)
            : createDefaultFormData(batchFiles);

         // Upload the batch
         const response = await fetch(uploadUrl, {
            method: 'POST',
            headers,
            body: formData,
         });

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Upload failed with status ${response.status}`);
         }

         const result = await response.json();
         results.push(result);

         completedFiles += batchFiles.length;

         // Calculate progress
         const overallProgress = Math.round((completedFiles / totalFiles) * 100);
         const currentBatchProgress = 100;

         // Call progress callback
         if (onProgress) {
            onProgress({
               currentBatch: batchIndex + 1,
               totalBatches,
               currentBatchProgress,
               overallProgress,
               completedFiles,
               totalFiles,
               failedFiles: 0,
            });
         }

         // Call batch complete callback
         if (onBatchComplete) {
            onBatchComplete(batchIndex, [result]);
         }
      } catch (error) {
         console.error(`Error uploading batch ${batchIndex + 1}:`, error);

         // Continue with next batch even if this one fails
         completedFiles += batchFiles.length;

         if (onProgress) {
            onProgress({
               currentBatch: batchIndex + 1,
               totalBatches,
               currentBatchProgress: 100,
               overallProgress: Math.round((completedFiles / totalFiles) * 100),
               completedFiles,
               totalFiles,
               failedFiles: batchFiles.length,
            });
         }
      }
   }

   return results;
}

/**
 * Helper function to create default FormData from files
 */
function createDefaultFormData(files: File[]): FormData {
   const formData = new FormData();
   files.forEach((file) => {
      formData.append('photos', file);
      formData.append('titles', file.name.split('.')[0]);
   });
   return formData;
}
















